import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import io from 'socket.io-client';
import PermisoDenegado from '../../components/PermisoDenegado';
import Loader from '../../components/Loader';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BASE_URL, { IMAGE_DOMAIN } from '../../config/config';
import Verificacion from '../../components/Verificacion';
import Suspendido from '../../components/Suspendido';

const Ticket = () => {
    const { identificador } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem('AuthToken');
    const tokenInfo = token ? jwtDecode(token) : {};
    const [ticket, setTicket] = useState({ chat: [] });
    const [usuarioCreador, setUsuarioCreador] = useState(null);
    const [usuarioAbridor, setUsuarioAbridor] = useState(null); // quien abrió el ticket (puede ser distinto al creador)
    const [content, setContent] = useState('');
    const [socket, setSocket] = useState(null);
    const [tecnicos, setTecnicos] = useState([]);
    const [usuarioEscribiendo, setUsuarioEscribiendo] = useState(null);
    const [permisosVerificados, setPermisosVerificados] = useState(false);
    const [permisoDenegado, setPermisoDenegado] = useState(false);
    const [isSending, setIsSending] = useState(false); // Estado para controlar "Enviando..."

    const [selectedFiles, setSelectedFiles] = useState([]); // Estado para los archivos seleccionados
    const [filesToUpload, setFilesToUpload] = useState([]); // Archivos para enviar al backend
    const [showAllFiles, setShowAllFiles] = useState(false);
    const [errorAlEnviar, setErrorAlEnviar] = useState(false);
    const [isDragging, setIsDragging] = useState(false); // Estado para drag and drop
    const [isDraggingOverChat, setIsDraggingOverChat] = useState(false); // Estado para drag sobre el chat
    const [expandedMessages, setExpandedMessages] = useState({}); // Estado para mensajes expandidos
    const [animatingMessages, setAnimatingMessages] = useState([]); // Estado para animaciones de mensajes
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Estado para colapsar sidebar - por defecto cerrado
    const [showTecnicoSelector, setShowTecnicoSelector] = useState(false); // Estado para mostrar selector de técnico
    const [searchTecnico, setSearchTecnico] = useState(''); // Estado para búsqueda de técnico
    const [filePreview, setFilePreview] = useState(null); // Estado para preview de archivo (url, tipo)

    const popupRef = useRef(null);
    const chatTicketRef = useRef(null);
    const tecnicoSelectorRef = useRef(null);

    // Configuración de ReactQuill sin imagen
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link'],
            ['clean']
        ]
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'link'
    ];


    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        processFiles(files);
    };

    const processFiles = (files) => {
        const maxFileSize = 5 * 1024 * 1024; // 5 MB
        const validFiles = [];
        const invalidFiles = [];

        files.forEach((file) => {
            if (file.size <= maxFileSize) {
                validFiles.push(file);
            } else {
                invalidFiles.push({ name: file.name, size: (file.size / (1024 * 1024)).toFixed(2) });
            }
        });

        if (invalidFiles.length > 0) {
            const errorMessage = invalidFiles
                .map((file) => `${file.name} (${file.size} MB)`)
                .join('\n');
            toast.error(
                `Los siguientes archivos exceden el tamaño permitido de 5 MB:\n${errorMessage}`,
                { position: "bottom-right", autoClose: 7000 }
            );
        }

        setSelectedFiles((prevFiles) => [...prevFiles, ...validFiles]);
        setFilesToUpload((prevFiles) => [...prevFiles, ...validFiles]);

    };

    // Drag and drop handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        setIsDraggingOverChat(false);

        const files = Array.from(e.dataTransfer.files);
        processFiles(files);
    };

    // Drag and drop handlers para el área del chat
    const handleChatDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.types.includes('Files')) {
            setIsDraggingOverChat(true);
        }
    };

    const handleChatDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Solo quitar el overlay si realmente salimos del chat-ticket
        if (chatTicketRef.current && !chatTicketRef.current.contains(e.relatedTarget)) {
            setIsDraggingOverChat(false);
        }
    };

    const handleChatDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleChatDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOverChat(false);

        const files = Array.from(e.dataTransfer.files);
        processFiles(files);
    };

    const handleRemoveFile = (index) => {
        setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
        setFilesToUpload((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const chatRef = useRef(null);
    const bottomRef = useRef(null);

    const processQuotes = (texto) => {
        const escapeHtml = (text) => {
            return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        };
        return texto.replace(/"([^"]*)"/g, (match, p1) => `"${escapeHtml(p1)}"`);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setShowAllFiles(false);
            }
            if (tecnicoSelectorRef.current && !tecnicoSelectorRef.current.contains(event.target)) {
                setShowTecnicoSelector(false);
            }
        };

        if (showAllFiles || showTecnicoSelector) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAllFiles, showTecnicoSelector]);


    useEffect(() => {
        const verificarPermisos = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/obtener-ticket/${identificador}`, {
                    headers: { Authorization: token }
                });
                const data = response.data;

                if (data.ticket) {
                    data.ticket.chat = Array.isArray(data.ticket.chat) ? data.ticket.chat : JSON.parse(data.ticket.chat);
                    setTicket(data.ticket);

                    if (data.ticket.usuario_creador !== tokenInfo.usuario && tokenInfo.role !== 'admin' && tokenInfo.role !== 'tecnico') {
                        setPermisoDenegado(true);
                    }
                }

                const usuarioResponse = await axios.get(`${BASE_URL}/usuario/info/${data.ticket.usuario_creador}`,
                    { headers: { Authorization: token } }
                );
                setUsuarioCreador(usuarioResponse.data.usuario);

                // Si el ticket fue abierto por alguien distinto al creador, cargar sus datos
                if (data.ticket.abierto_por && data.ticket.abierto_por !== data.ticket.usuario_creador) {
                    const abridorResponse = await axios.get(`${BASE_URL}/usuario/info/${data.ticket.abierto_por}`,
                        { headers: { Authorization: token } }
                    );
                    setUsuarioAbridor(abridorResponse.data.usuario);
                }
            } catch (error) {
                console.error('Error al obtener el ticket:', error);
                // 403 = ticket existe pero no pertenece al usuario
                if (error.response?.status === 403) {
                    setPermisoDenegado(true);
                }
            } finally {
                setPermisosVerificados(true);
            }
        };

        verificarPermisos();
    }, [identificador, tokenInfo.usuario, tokenInfo.role]);

    useEffect(() => {
        const fetchTecnicos = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/usuario/tecnicos`, {
                    headers: { Authorization: token }
                });
                setTecnicos(response.data.usuarios);
            } catch (error) {
                console.error('Error al obtener la lista de técnicos:', error);
            }
        };

        fetchTecnicos();
    }, []);

    // Mover mensajesConDescripcion aquí para evitar error de inicialización
    const mensajesConDescripcion = useMemo(() => {
        if (!ticket) return [];

        // 1) Mensaje "sintético" con la descripción del ticket
        // Si el ticket fue abierto por un técnico/admin en nombre de otro usuario,
        // el primer mensaje aparece firmado por el abridor, no por el usuario_creador
        const autor = usuarioAbridor || usuarioCreador;
        const primerMensaje = {
            usuario: autor?.usuario || ticket.usuario_creador || 'desconocido',
            nombre: autor?.nombre || ticket.nombre || '',
            apellidos: autor?.apellidos || ticket.apellidos || '',
            fotoPerfil: autor?.fotoPerfil || '/images/usuario.png',
            role: autor?.role || 'user',
            texto: ticket.descripcion || '',
            fecha: ticket.createdAt || new Date().toISOString(),
            archivos: ticket.archivos ? ticket.archivos.split(',') : [],
            __esDescripcion: true,
        };

        // 2) Resto de mensajes del chat ordenados por fecha ascendente
        const chatOrdenado = (Array.isArray(ticket.chat) ? ticket.chat : []).slice().sort((a, b) => {
            const fa = new Date(a.fecha).getTime();
            const fb = new Date(b.fecha).getTime();
            return fa - fb;
        });

        return [primerMensaje, ...chatOrdenado];
    }, [ticket, usuarioCreador, usuarioAbridor]);

    const scrollToBottom = (instant = false) => {
        if (chatRef.current) {
            if (instant) {
                // Con column-reverse, scroll a 0 es el final
                chatRef.current.scrollTop = 0;
            } else {
                // Scroll suave al final (que es scrollTop = 0 con column-reverse)
                chatRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        }
    };

    const scrollToTop = () => {
        if (chatRef.current) {
            chatRef.current.scrollTop = 0;
        }
    };

    // Con column-reverse, el scroll ya comienza al final naturalmente
    // Solo necesitamos scroll suave cuando llegan nuevos mensajes
    useEffect(() => {
        if (ticket.chat.length > 0 && chatRef.current) {
            // Scroll suave cuando llegan mensajes nuevos
            const timeout = setTimeout(() => {
                scrollToBottom(false);
            }, 200);
            return () => clearTimeout(timeout);
        }
    }, [ticket.chat.length]);

    const handleChange = (value) => {
        setContent(value);
        if (socket) {
            socket.emit('escribiendo', {
                identificador: identificador,
                usuario: tokenInfo.usuario,
                nombre: tokenInfo.nombre,
                apellidos: tokenInfo.apellidos,
            });
        }
    };

    const splitFiles = (filesString) => {
        return filesString.split(",");
    };

    const getFileName = (url, length = 10) => {
        const fileName = url.substring(url.lastIndexOf('/') + 1);
        const extensionIndex = fileName.lastIndexOf('.');
        const name = fileName.substring(0, extensionIndex);
        const extension = fileName.substring(extensionIndex);
        const shortenedName = name.length > length ? name.substring(0, length) + '...' : name;
        return shortenedName + extension;
    };

    const getIconUrl = (file) => {
        const extension = file.split('.').pop().toLowerCase();
        switch (extension) {
            case 'png':
            case 'jpg':
            case 'jpeg':
                return '/images/imagen-icono.svg';
            case 'xlsx':
                return '/images/excel.svg';
            case 'psd':
            case 'gif':
            case 'zip':
            case 'docx':
            case 'doc':
            case 'svg':
            case 'pdf':
            case 'csv':
                return `/images/${extension}.svg`;
            default:
                return '/images/almacenamiento-de-archivos.svg';
        }
    };

    const copiarTicket = () => {
        const cleanHTML = (html) => {
            if (!html) return 'No especificada';
            const doc = new DOMParser().parseFromString(String(html), 'text/html');
            return (doc.body.textContent || '').trim();
        };

                // Texto limpio (plain) con separadores legibles
                const plainTextParts = [];
                plainTextParts.push(`Ticket ID: #${ticket.identificador}`);
                plainTextParts.push(`Asunto: ${ticket.asunto || 'No especificado'}`);
                plainTextParts.push(`Descripción:\n${cleanHTML(ticket.descripcion)}`);
                plainTextParts.push(`Prioridad: ${ticket.prioridad === 'urgente' ? 'Urgente' : 'No urgente'}`);
                plainTextParts.push('---');
                plainTextParts.push('Datos del cliente:');
                plainTextParts.push(`Nombre: ${ticket.nombre || '—'} ${ticket.apellidos || ''}`);
                plainTextParts.push(`Email: ${ticket.email || '—'}`);
                plainTextParts.push(`Teléfono: ${ticket.telefono || '—'}`);
                plainTextParts.push(`Webs: ${ticket.webs_del_cliente || '—'}`);

                const plainText = plainTextParts.join('\n');

                // HTML completo compatible: documento mínimo con meta charset y estilos inline
                const safeDescription = (ticket.descripcion && typeof ticket.descripcion === 'string')
                        ? ticket.descripcion
                        : (ticket.descripcion ? String(ticket.descripcion) : '');

                const htmlDoc = `
                <html>
<head>
    <meta charset="utf-8" />
    <title>Ticket #${ticket.identificador}</title>
</head>
<body style="margin:0; padding:30px; background:#f4f6fb; font-size:14px;">

    <div style="max-width:700px; margin:0 auto; background:#ffffff; border:1px solid #e1e5f2; padding:25px;">

        <!-- HEADER -->
        <div style="border-bottom:2px solid #2c41a3; padding-bottom:15px; margin-bottom:20px;">
            
            <div style="font-weight:bold; color:#2c41a3; margin-bottom:8px; width: fit-content;">TICKET DE SOPORTE #${ticket.identificador}</div>

            <div style="margin-bottom:10px;">
                <strong>Asunto:</strong> ${escapeHtml(ticket.asunto || 'No especificado')}
            </div>

            <!-- DESCRIPCIÓN JUSTO DEBAJO -->
            <div>
                <strong>Descripción:</strong>
                <div style="margin-top:6px; white-space:pre-wrap; line-height:1.5;">
                    ${safeDescription ? sanitizeHtmlFragment(safeDescription) : 'No especificada'}
                </div>
            </div>

        </div>

        <!-- RESUMEN -->
        <div style="margin-bottom:25px;">
            <div style="font-weight:bold; color:#2c41a3; margin-bottom:8px; text-decoration:underline;">Resumen</div>

            <div>
                <strong>Prioridad:</strong> 
                ${ticket.prioridad === 'urgente'
                    ? '<span style="font-weight:bold; color:#2c41a3;">URGENTE</span>'
                    : 'No urgente'}
            </div>
        </div>

        <!-- CLIENTE -->
        <div>
            <div style="font-weight:bold; color:#2c41a3; margin-bottom:8px; text-decoration:underline;">Datos del cliente</div>

            <div style="margin-bottom:6px;">
                <strong>Nombre:</strong> ${escapeHtml(`${ticket.nombre || '—'} ${ticket.apellidos || ''}`)}
            </div>

            <div style="margin-bottom:6px;">
                <strong>Email:</strong> ${escapeHtml(ticket.email || '—')}
            </div>

            <div style="margin-bottom:6px;">
                <strong>Teléfono:</strong> ${escapeHtml(ticket.telefono || '—')}
            </div>

            <div>
                <strong>Webs:</strong> ${escapeHtml(ticket.webs_del_cliente || '—')}
            </div>
        </div>

    </div>

</body>
</html>
`;

                // Helpers para sanear/escapar HTML antes de inyectarlo en el documento
                function escapeHtml(str) {
                        if (!str && str !== 0) return '';
                        return String(str)
                                .replace(/&/g, '&amp;')
                                .replace(/</g, '&lt;')
                                .replace(/>/g, '&gt;')
                                .replace(/\"/g, '&quot;')
                                .replace(/'/g, '&#39;');
                }

                // Sanitiza fragmentos HTML simples: permitimos etiquetas básicas (p, br, b, i, strong, em, ul, ol, li, a)
                function sanitizeHtmlFragment(html) {
                    if (!html) return '';
                    // Parse and rebuild allowing only a small set of tags
                    const doc = new DOMParser().parseFromString(String(html), 'text/html');
                    const allowed = ['P','BR','B','STRONG','I','EM','UL','OL','LI','A'];
                    const walk = (node) => {
                        const nodeName = node.nodeName;
                        if (node.nodeType === Node.TEXT_NODE) {
                            // Collapse internal whitespace and trim to avoid invisible spacing
                            return escapeHtml(node.textContent).replace(/\s+/g, ' ').trim();
                        }
                        if (!allowed.includes(nodeName)) {
                            // unwrap children
                            return Array.from(node.childNodes).map(walk).join('');
                        }
                        if (nodeName === 'A') {
                            const href = node.getAttribute('href') ? escapeHtml(node.getAttribute('href')) : '#';
                            const text = Array.from(node.childNodes).map(walk).join('');
                            return `<a href="${href}">${text}</a>`;
                        }
                        // For block/inline tags, keep basic structure
                        const inner = Array.from(node.childNodes).map(walk).join('');
                        // Trim surrounding whitespace inside blocks
                        const trimmedInner = inner.replace(/\s+/g, ' ').trim();
                        return `<${nodeName.toLowerCase()}>${trimmedInner}</${nodeName.toLowerCase()}>`;
                    };
                    return Array.from(doc.body.childNodes).map(walk).join('');
                }

                // Normalize htmlDoc: remove inter-tag whitespace and trim
                const normalizedHtml = htmlDoc.replace(/>\s+</g, '><').trim();

                // Clean up plain text description: normalize newlines and collapse multiple blank lines
                const cleanedDescription = (cleanHTML(ticket.descripcion) || '')
                    .replace(/\r\n/g, '\n')
                    .replace(/\n\s*\n+/g, '\n\n')
                    .replace(/[ \t]{2,}/g, ' ')
                    .trim();

                const normalizedPlainText = plainText.replace(cleanHTML(ticket.descripcion), cleanedDescription);

                // Escribir en el portapapeles tanto html como texto plano
                navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([normalizedHtml], { type: 'text/html' }),
                        'text/plain': new Blob([normalizedPlainText], { type: 'text/plain' })
                    })
                ]).then(() => {
                    toast.success('Información del ticket copiada.', { position: 'bottom-right' });
                }).catch((err) => {
                    console.error('Error copiando al portapapeles:', err);
                    toast.error('Error al copiar la información del ticket.', { position: 'bottom-right' });
                });
    };

    const descargarArchivo = async (url) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = url.split('/').pop() || 'archivo';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error al descargar el archivo:', error);
            toast.error("Error al descargar el archivo.", { position: "bottom-right" });
        }
    };



    const actualizarDpto = async (nuevoDpto) => {
        try {
            await axios.put(`${BASE_URL}/actualizar-dpto`, { identificador, dpto: nuevoDpto }, {
                headers: { Authorization: token }
            });
            setTicket((prevTicket) => ({
                ...prevTicket,
                dpto: nuevoDpto,
            }));
        } catch (error) {
            console.error('Error al actualizar el dpto:', error);
        }
    };

    const actualizarPrioridad = async (nuevaPrioridad) => {
        try {
            await axios.put(`${BASE_URL}/actualizar-prioridad`, { identificador, prioridad: nuevaPrioridad }, {
                headers: { Authorization: token }
            });
            setTicket((prevTicket) => ({
                ...prevTicket,
                prioridad: nuevaPrioridad,
            }));
        } catch (error) {
            console.error('Error al actualizar la prioridad:', error);
        }
    };

    const actualizarEstadoDelTicket = async (nuevoEstado) => {
        try {
            await axios.put(`${BASE_URL}/actualizar-estado_del_ticket`, { identificador, estado_del_ticket: nuevoEstado }, {
                headers: { Authorization: token }
            });
            setTicket((prevTicket) => ({
                ...prevTicket,
                estado_del_ticket: nuevoEstado,
            }));

            if (nuevoEstado === 'cerrado' && socket) {
                socket.emit('cerrarTicket', { identificador });
                const notificarCierre = await axios.post(`${BASE_URL}/notificar-cierre-cliente`, {
                    identificador,
                    usuario_creador: ticket.usuario_creador,
                    nombre_cliente: ticket.nombre,
                    asunto: ticket.asunto,
                }, {
                    headers: { Authorization: token }
                });
            }
        } catch (error) {
            console.error('Error al actualizar el estado del ticket:', error);
        }
    };

    const actualizarTecnicoAsignado = async (usuarioEncargado) => {
        try {
            const response = await axios.put(`${BASE_URL}/actualizar-tecnico`, {
                identificador,
                usuario_encargado: usuarioEncargado
            }, {
                headers: { Authorization: token }
            });

            setTicket((prevTicket) => ({
                ...prevTicket,
                usuario_encargado: usuarioEncargado
            }));


            if (usuarioEncargado !== "sin_asignar") {
                const notificarResponse = await axios.post(`${BASE_URL}/notificar-tecnico`, {
                    identificador,
                    usuario_encargado: usuarioEncargado,
                    usuario_notificador: tokenInfo.usuario,
                    usuario_creador: ticket.usuario_creador,
                    nombre_cliente: ticket.nombre,
                    apellidos_cliente: ticket.apellidos,
                    email_cliente: ticket.email,
                    webs_del_cliente: ticket.webs_del_cliente,
                    asunto: ticket.asunto,
                    descripcion: ticket.descripcion
                }, {
                    headers: { Authorization: token }
                });

            } else {
            }

        } catch (error) {
            console.error('Error al asignar o notificar al técnico del ticket:', error.response ? error.response.data : error.message);
        }
    };



    const obtenerNombreTecnicoAsignado = () => {
        if (ticket.usuario_encargado === 'sin_asignar') return null;
        const tecnico = tecnicos.find(t => t.usuario === ticket.usuario_encargado);
        return tecnico
            ? `${tecnico.nombre} ${tecnico.apellidos} (@${tecnico.usuario})`
            : `@${ticket.usuario_encargado}`;
    };

    // Filtrar técnicos por búsqueda
    const tecnicosFiltrados = tecnicos.filter(t => {
        const searchLower = searchTecnico.toLowerCase();
        return (
            t.nombre.toLowerCase().includes(searchLower) ||
            t.apellidos.toLowerCase().includes(searchLower) ||
            t.usuario.toLowerCase().includes(searchLower)
        );
    });

    // Función para seleccionar técnico
    const seleccionarTecnico = (usuario) => {
        actualizarTecnicoAsignado(usuario);
        setShowTecnicoSelector(false);
        setSearchTecnico('');
    };

    // Función para agrupar mensajes por fecha
    const agruparMensajesPorFecha = (mensajes) => {
        const grupos = [];
        let fechaActual = null;

        mensajes.forEach((mensaje) => {
            const fechaMensaje = new Date(mensaje.fecha).toLocaleDateString('es-ES', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
            });

            if (fechaMensaje !== fechaActual) {
                grupos.push({ tipo: 'separador', fecha: fechaMensaje });
                fechaActual = fechaMensaje;
            }

            grupos.push({ tipo: 'mensaje', data: mensaje });
        });

        return grupos;
    };

    // Función para determinar si debe mostrar avatar (último del grupo)
    const deberMostrarAvatar = (mensaje, siguienteMensaje) => {
        if (!siguienteMensaje) return true;
        return mensaje.usuario !== siguienteMensaje.usuario;
    };

    // Función para toggle de información expandida
    const toggleMensajeExpandido = (index) => {
        setExpandedMessages(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };


    const enviarMensaje = async () => {
        // Evitamos que se envíe si ya se está procesando un envío
        if (isSending) return;

        if ((!content || content.trim() === "" || content === "<p><br></p>") && filesToUpload.length === 0) {
            setErrorAlEnviar(true);
            return;
        }

        // Activamos el estado "Enviando..."
        setIsSending(true);

        try {
            let uploadedFiles = [];

            if (filesToUpload.length > 0) {
                const formData = new FormData();
                filesToUpload.forEach(file => formData.append('archivoschat', file));

                const response = await axios.post(`${BASE_URL}/subir-archivos-chat/${identificador}`, formData, {
                    headers: { 
                        'Content-Type': 'multipart/form-data',
                        Authorization: token
                    },
                });

                uploadedFiles = response.data.archivosNuevos;
            }

            const processedContent = processQuotes(content);

            // IMPORTANTE: Usar identificador de useParams, NO del estado ticket
            const mensajeData = {
                identificador: identificador, // <- Cambio crítico: usar identificador de la URL
                mensaje: processedContent,
                usuario: tokenInfo.usuario,
                nombre: tokenInfo.nombre,
                apellidos: tokenInfo.apellidos,
                role: tokenInfo.role,
                fotoPerfil: tokenInfo.fotoPerfil,
                archivos: uploadedFiles,
            };


            // Verificar si el socket está conectado, si no reconectar
            if (!socket || !socket.connected) {
                const newSocket = io(`${BASE_URL}`);
                setSocket(newSocket);
                
                // Esperar a que se conecte y unirse a la sala
                newSocket.on('connect', () => {
                    newSocket.emit('joinRoom', {
                        identificador: identificador, // <- Usar identificador de la URL
                        usuario: tokenInfo.usuario
                    });
                    newSocket.emit('mensaje', mensajeData);
                });
            } else {
                socket.emit('mensaje', mensajeData);
            }

            // Actualización optimista: agregar mensaje temporalmente
            // Se marcará con un flag temporal para reemplazarlo cuando llegue del servidor
            const tempMessage = {
                usuario: mensajeData.usuario,
                nombre: mensajeData.nombre,
                apellidos: mensajeData.apellidos,
                role: mensajeData.role,
                fotoPerfil: mensajeData.fotoPerfil,
                texto: processedContent, // Usar 'texto' en lugar de 'mensaje'
                fecha: new Date().toISOString(), // Usar 'fecha' en lugar de 'createdAt'
                archivos: uploadedFiles,
                _isTemp: true,
                _tempId: Date.now()
            };

            // Añadir animación al mensaje
            const tempId = tempMessage._tempId;
            setAnimatingMessages(prev => [...prev, tempId]);
            setTimeout(() => {
                setAnimatingMessages(prev => prev.filter(id => id !== tempId));
            }, 500);

            setTicket((prevTicket) => {
                let nuevoEstadoChat = 'sin_responder';
                if (tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico') {
                    nuevoEstadoChat = 'respondido';
                }

                return {
                    ...prevTicket,
                    chat: [...prevTicket.chat, tempMessage],
                    estado_del_chat: nuevoEstadoChat
                };
            });

            setErrorAlEnviar(false);
            setContent('');
            setSelectedFiles([]);
            setFilesToUpload([]);
            
            // Resetear el input file
            const fileInput = document.getElementById('file');
            if (fileInput) fileInput.value = '';
        } catch (error) {
            console.error('Error al enviar el mensaje:', error);
        }



        // Establece un cooldown de 3 segundos antes de permitir otro envío
        setTimeout(() => {
            setIsSending(false);
        }, 3000);
    };

    useEffect(() => {
        const newSocket = io(`${BASE_URL}`);
        setSocket(newSocket);

        newSocket.on('ticketCerrado', (data) => {
            setTicket((prevTicket) => ({
                ...prevTicket,
                estado_del_ticket: 'cerrado'
            }));
        });

        return () => {
            newSocket.disconnect();
        };
    }, []);

    useEffect(() => {
        if (socket && identificador) {
            
            socket.emit('joinRoom', {
                identificador: identificador,
                usuario: tokenInfo.usuario
            });
            
            // Cleanup: salir de la sala cuando el componente se desmonte o cambie el identificador
            return () => {
                socket.emit('leaveRoom', {
                    identificador: identificador,
                    usuario: tokenInfo.usuario
                });
            };
        }
    }, [identificador, socket]);

    useEffect(() => {
        if (socket) {
            // Limpiar listeners anteriores para evitar duplicados
            socket.off('mensaje');
            
            socket.on('mensaje', (mensaje) => {
                
                setTicket((prevTicket) => {
                    // Eliminar mensaje temporal si existe
                    const chatSinTemp = prevTicket.chat.filter(m => {
                        // Si es un mensaje temporal con el mismo contenido, eliminarlo
                        if (m._isTemp && m.usuario === mensaje.usuario) {
                            // Comparar el texto del mensaje
                            const textoTemp = m.texto || '';
                            const textoMensaje = mensaje.texto || mensaje.mensaje || '';
                            if (textoTemp === textoMensaje) {
                                return false;
                            }
                        }
                        return true;
                    });

                    // Verificar si el mensaje real ya existe (prevenir duplicados)
                    const mensajeExiste = chatSinTemp.some(m => {
                        // Si ambos tienen ID, comparar por ID
                        if (m.id && mensaje.id) {
                            return m.id === mensaje.id;
                        }
                        // Si no, comparar por contenido y usuario exacto
                        const textoM = m.texto || m.mensaje || '';
                        const textoMensaje = mensaje.texto || mensaje.mensaje || '';
                        const fechaM = m.fecha || m.createdAt;
                        const fechaMensaje = mensaje.fecha || mensaje.createdAt;
                        
                        return m.usuario === mensaje.usuario && 
                               textoM === textoMensaje && 
                               fechaM === fechaMensaje &&
                               !m._isTemp;
                    });

                    if (mensajeExiste) {
                        return prevTicket;
                    }

                    let nuevoEstadoChat = prevTicket.estado_del_chat;

                    if (mensaje.role === 'user') {
                        nuevoEstadoChat = 'sin_responder';
                    } else if (mensaje.role === 'admin' || mensaje.role === 'tecnico') {
                        nuevoEstadoChat = 'respondido';
                    }

                    // Si el técnico asignado cambia (opcional, solo si lo necesitas)
                    const nuevoUsuarioEncargado = mensaje.usuario_encargado || prevTicket.usuario_encargado;

                    // Si el ticket estaba cerrado y el mensaje viene de un user, reabrirlo en local
                    const nuevoEstadoTicket =
                        prevTicket.estado_del_ticket === 'cerrado' && mensaje.role === 'user'
                            ? 'abierto'
                            : prevTicket.estado_del_ticket;

                    // Normalizar el mensaje para asegurar que tenga el formato correcto
                    const mensajeNormalizado = {
                        ...mensaje,
                        texto: mensaje.texto || mensaje.mensaje, // Asegurar que tenga 'texto'
                        fecha: mensaje.fecha || mensaje.createdAt || new Date().toISOString() // Asegurar que tenga 'fecha'
                    };

                    return {
                        ...prevTicket,
                        chat: [...chatSinTemp, mensajeNormalizado],
                        estado_del_chat: nuevoEstadoChat,
                        estado_del_ticket: nuevoEstadoTicket,
                        usuario_encargado: nuevoUsuarioEncargado
                    };
                });
            });



            let typingTimeout;
            socket.on('escribiendo', (data) => {
                if (data.usuario !== tokenInfo.usuario) {
                    setUsuarioEscribiendo(`${data.nombre} ${data.apellidos} está escribiendo...`);
                    clearTimeout(typingTimeout);
                    typingTimeout = setTimeout(() => {
                        setUsuarioEscribiendo(null);
                    }, 3000);
                }
            });
        }

        return () => {
            if (socket) {
                socket.off('mensaje');
                socket.off('escribiendo');
            }
        };
    }, [socket]);

    if (!permisosVerificados) {
        return <Loader />;
    }

    if (tokenInfo.estado === 'verificar') return <Verificacion />;
    if (tokenInfo.estado === 'suspendido') return <Suspendido />;

    if (permisoDenegado) {
        return <PermisoDenegado />;
    }

    return (
        <main className='ticket site-width'>
            {ticket && (
                <>
                <div className='cabecera-ticket'>
                    <div className="cabecera-ticket-uno">
                        {tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico' ? (
                        <button className="boton-volver" onClick={() => navigate('/dashboard/tickets')}>
                            ←
                        </button>
                        ) : (
                        <button className="boton-volver" onClick={() => navigate('/dashboard')}>
                            ←
                        </button>
                        )}                            
                            <div className="cabecera-ticket-texto">
                                {ticket.estado_del_ticket === 'cerrado' ? (
                                    <>
                                        <h1><span className='marcador-cerrado'></span>Ticket #{ticket.identificador}</h1>
                                        <p>{ticket.asunto}</p>
                                    </>
                                ) : (
                                    <>
                                        <h1><span className={`marcador-abierto ${ticket.estado_del_ticket == 'retenido' ? 'marcador-retenido' : ''}`}></span>Ticket #{ticket.identificador}</h1>
                                        <p>{ticket.asunto}</p>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="cabecera-ticket-dos">
                            <div className="tecnico-asignado">
                                {ticket.usuario_encargado === "sin_asignar" ? <img src="/images/sin_asignar.svg" alt="Usuario sin asignar" /> : <img src="/images/asignado.svg" alt="Usuario asignado" />}
                                {tokenInfo.role === "admin" || tokenInfo.role === "tecnico" ? (
                                    <div className="tecnico-selector-container">
                                        <div 
                                            className="tecnico-selector-trigger"
                                            onClick={() => setShowTecnicoSelector(!showTecnicoSelector)}
                                        >
                                            <span>
                                                {ticket.usuario_encargado === "sin_asignar" 
                                                    ? "Sin asignar" 
                                                    : obtenerNombreTecnicoAsignado()}
                                            </span>
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                                                <path d="M6 8L2 4h8z"/>
                                            </svg>
                                        </div>
                                        
                                        {showTecnicoSelector && (
                                            <div className="tecnico-dropdown" ref={tecnicoSelectorRef}>
                                                <div className="tecnico-search">
                                                    <input
                                                        type="text"
                                                        placeholder="Buscar técnico..."
                                                        value={searchTecnico}
                                                        onChange={(e) => setSearchTecnico(e.target.value)}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="tecnico-list">
                                                    <div 
                                                        className={`tecnico-item ${ticket.usuario_encargado === "sin_asignar" ? 'selected' : ''}`}
                                                        onClick={() => seleccionarTecnico("sin_asignar")}
                                                    >
                                                        <img src="/images/sin_asignar.svg" alt="Sin asignar" />
                                                        <span>Sin asignar</span>
                                                    </div>
                                                    {tecnicosFiltrados.map((tecnico) => (
                                                        <div 
                                                            key={tecnico.usuario}
                                                            className={`tecnico-item ${ticket.usuario_encargado === tecnico.usuario ? 'selected' : ''}`}
                                                            onClick={() => seleccionarTecnico(tecnico.usuario)}
                                                        >
                                                            <img src="/images/asignado.svg" alt={tecnico.nombre} />
                                                            <div className="tecnico-info">
                                                                <span className="tecnico-nombre">{tecnico.nombre} {tecnico.apellidos}</span>
                                                                <span className="tecnico-usuario">@{tecnico.usuario}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {tecnicosFiltrados.length === 0 && (
                                                        <div className="tecnico-item no-results">
                                                            No se encontraron técnicos
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <p>
                                            {ticket.usuario_encargado === "sin_asignar"
                                                ? 'Sin asignar'
                                                :
                                                <>
                                                    <b>Técnico asignado:</b><br />
                                                    {obtenerNombreTecnicoAsignado()}

                                                </>

                                            }
                                        </p>
                                    </>
                                )}
                            </div>
                            <div className={`estado-del-ticket ${ticket.estado_del_chat === "respondido" ? 'ticket-respondido' : ''}`}>
                                <p>
                                    {ticket.estado_del_chat === "sin_responder" ? 'Esperando respuesta' : ''}
                                    {ticket.estado_del_chat === "respondido" ? 'Respondido' : ''}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className={`grid-ticket ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                        <div className={`datos-ticket ${sidebarCollapsed ? 'collapsed' : ''}`}>
                            <div className="datos-principales">
                                <div className="mini-descripcion-ticket">
                                    <h3>{ticket.asunto}</h3>
                                    <div className='mini-data'>
                                        <p>#{ticket.identificador}</p>
                                        <p>
                                            {new Date(ticket.createdAt).toLocaleString('es-ES', {
                                                day: '2-digit',
                                                month: 'long',
                                                year: 'numeric',
                                                hour: 'numeric',
                                                minute: '2-digit',
                                                hour12: false
                                            }).replace(',', ' a las')}
                                        </p>
                                    </div>
                                </div>
                                <div className="grid-bloques-datos">
                                    <div className="bloque-datos">
                                        <p>Nombre completo</p>
                                        <p>{ticket.nombre} {ticket.apellidos}</p>
                                    </div>
                                    <div className="bloque-datos">
                                        <p>Email</p>
                                        <p>{ticket.email}</p>
                                    </div>
                                    <div className="bloque-datos">
                                        <p>Teléfono</p>
                                        <p>{ticket.telefono}</p>
                                    </div>
                                    <div className="bloque-datos">
                                        <p>Webs en mantenimiento</p>
                                        <p>{ticket.webs_del_cliente}</p>
                                    </div>
                                </div>
                                <hr />
                                <div className="grid-bloques-gestion">
                                    <h3>Gestión de tu ticket</h3>
                                    <p>Aquí mostramos qué departamento se encarga de tu ticket, qué estado tiene y cuál es su prioridad.</p>
                                    <div className="grid-gestion-datos">
                                        <div className="bloque-datos">
                                            <p>Departamento</p>
                                            {tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico' ? (
                                                <select value={ticket.dpto} onChange={(e) => actualizarDpto(e.target.value)}>
                                                    <option value="no_especificado">No especificado</option>
                                                    <option value="desarrollo">Desarrollo</option>
                                                    <option value="diseno">Diseño</option>
                                                    <option value="marketing">Marketing</option>
                                                    <option value="redes_sociales">Redes Sociales</option>
                                                </select>
                                            ) : ticket.dpto === 'no_especificado' ? (
                                                <p className='no-especificado'>No especificado</p>
                                            ) : ticket.dpto === 'desarrollo' ? (
                                                <p className="desarrollo">Desarrollo</p>
                                            ) : ticket.dpto === 'diseno' ? (
                                                <p className="diseno">Diseño</p>
                                            ) : ticket.dpto === 'marketing' ? (
                                                <p className="marketing">Marketing</p>
                                            ) : ticket.dpto === 'redes_sociales' ? (
                                                <p className="redes-sociales">Redes Sociales</p>
                                            ) : (
                                                ''
                                            )}
                                        </div>
                                        <div className="bloque-datos color-dato">
                                            <p>Prioridad</p>
                                            {tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico' ? (
                                                <select value={ticket.prioridad} onChange={(e) => actualizarPrioridad(e.target.value)}>
                                                    <option value="no_urgente">No urgente</option>
                                                    <option value="urgente">Urgente</option>
                                                </select>
                                            ) : ticket.prioridad === 'no_urgente' ? (
                                                <p className='sin-prioridad'>No Urgente</p>
                                            ) : ticket.prioridad === 'urgente' ? (
                                                <p className="alta">Urgente</p>
                                            ) : (
                                                ''
                                            )}
                                        </div>
                                        <div className="bloque-datos">
                                            <p>Estado</p>
                                            {tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico' ? (
                                                <select value={ticket.estado_del_ticket} onChange={(e) => actualizarEstadoDelTicket(e.target.value)}>
                                                    <option value="abierto">Abierto</option>
                                                    <option value="retenido">Retenido</option>
                                                    <option value="cerrado">Cerrado</option>
                                                </select>
                                            ) : ticket.estado_del_ticket === 'abierto' ? (
                                                <p className="abierto">Abierto</p>
                                            ) : ticket.estado_del_ticket === 'retenido' ? (
                                                <p className="retenido">Retenido</p>
                                            ) : ticket.estado_del_ticket === 'cerrado' ? (
                                                <p className="cerrado">Cerrado</p>
                                            ) : (
                                                ''
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {tokenInfo.role === "admin" && (
                                    <>
                                        <hr />
                                        <div className="acciones-ticket-admin">
                                            <h3>Acciones de administración</h3>
                                            <p>Solo visible para administradores</p>
                                            <button onClick={copiarTicket} className="copiar-ticket">
                                                <img src="/images/copia.svg" alt="Copiar" />
                                                <p>Copiar información del ticket</p>
                                            </button>
                                        </div>
                                    </>

                                )}
                            </div>
                            <div className='datos-archivos'>
                                <p>Archivos aportados</p>
                                {ticket.archivos ? (
                                    <>
                                        <div className='grid-archivos'>
                                            {splitFiles(ticket.archivos)
                                                .slice(0, 3)
                                                .map((file, index) => (
                                                    <a target='_blank' href={file} key={index} className='archivo-ticket' download>
                                                        <img src={getIconUrl(file)} alt={`Archivo ${getFileName(file)}`} />
                                                        <p>{getFileName(file)}</p>
                                                        <img className='descargar' src="/images/descargar.svg" alt="Descargar" />
                                                    </a>
                                                ))}
                                        </div>
                                        {splitFiles(ticket.archivos).length > 3 && (
                                            <button className='ver-todos' onClick={() => setShowAllFiles(true)}>
                                                Ver todos
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <p className='no-archivos'>No se añadieron archivos</p>
                                )}
                                {showAllFiles && (
                                    <>
                                        <div className="layer-black"></div>
                                        <div className='popup-archivos'>
                                            <div className='popup-contenido' ref={popupRef}>
                                                <button className='cerrar-popup' onClick={() => setShowAllFiles(false)}>x</button>
                                                <h3>Archivos incluídos en el ticket</h3>
                                                <div className='grid-archivos-pop-up'>
                                                    {splitFiles(ticket.archivos).map((file, index) => (
                                                        <a target='_blank' href={file} key={index} className='archivo-ticket' download>
                                                            <img src={getIconUrl(file)} alt={`Archivo ${getFileName(file)}`} />
                                                            <p>{getFileName(file)}</p>
                                                            <img className='descargar' src="/images/descargar.svg" alt="Descargar" />
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        <div 
                            className="chat-ticket"
                            ref={chatTicketRef}
                            onDragEnter={handleChatDragEnter}
                            onDragOver={handleChatDragOver}
                            onDragLeave={handleChatDragLeave}
                            onDrop={handleChatDrop}
                        >
                            <div className="chat-header-bar">
                                <button 
                                    className={`toggle-sidebar-btn ${sidebarCollapsed ? 'collapsed' : ''}`}
                                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                                    title={sidebarCollapsed ? 'Mostrar información del ticket' : 'Ocultar información'}
                                >
                                    {sidebarCollapsed ? (
                                        <>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <rect x="3" y="3" width="7" height="18" rx="1"/>
                                                <path d="M14 8l4 4-4 4"/>
                                            </svg>
                                            <span>Mostrar info</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <rect x="3" y="3" width="7" height="18" rx="1"/>
                                                <path d="M18 8l-4 4 4 4"/>
                                            </svg>
                                            <span>Ocultar</span>
                                        </>
                                    )}
                                </button>
                                <div className="chat-title">
                                    <h3>Conversación</h3>
                                    <span className="message-count">{mensajesConDescripcion.length} mensajes</span>
                                </div>
                            </div>
                            {isDraggingOverChat && (
                                <div className="drag-overlay-chat">
                                    <div className="drag-overlay-content">
                                        <img src="/images/archive.svg" alt="Soltar archivos" />
                                        <p>Suelta los archivos aquí para adjuntarlos</p>
                                    </div>
                                </div>
                            )}
                            <div className="chat" ref={chatRef}>
                                {agruparMensajesPorFecha(mensajesConDescripcion).reverse().map((item, index, array) => {
                                    if (item.tipo === 'separador') {
                                        return (
                                            <div key={`sep-${index}`} className="chat-date-separator">
                                                <span>{item.fecha}</span>
                                            </div>
                                        );
                                    }

                                    const msg = item.data;
                                    const esMio = msg.usuario === tokenInfo.usuario;
                                    const siguienteMensaje = array[index + 1]?.tipo === 'mensaje' ? array[index + 1].data : null;
                                    const mostrarAvatar = deberMostrarAvatar(msg, siguienteMensaje);
                                    const esAnimado = animatingMessages.includes(msg._tempId);
                                    const estaExpandido = expandedMessages[index];

                                    return (
                                        <div
                                            key={msg._tempId || index}
                                            className={`whatsapp-message ${esMio ? 'message-own' : 'message-other'} ${esAnimado ? 'message-animating' : ''} ${!mostrarAvatar ? 'message-grouped' : ''}`}
                                        >
                                            <div className="message-container">
                                                {!esMio && mostrarAvatar && (
                                                    <img 
                                                        className="message-avatar" 
                                                        src={msg.fotoPerfil ? (msg.fotoPerfil.startsWith('http') ? msg.fotoPerfil : `${IMAGE_DOMAIN}${msg.fotoPerfil}`) : `${IMAGE_DOMAIN}/images/usuario.png`} 
                                                        alt="Avatar" 
                                                    />
                                                )}
                                                {!esMio && !mostrarAvatar && <div className="message-avatar-spacer"></div>}
                                                
                                                <div className="message-bubble" onClick={() => toggleMensajeExpandido(index)}>
                                                    {!esMio && (
                                                        <p className='message-sender-name'>{msg.nombre} {msg.apellidos}</p>
                                                    )}
                                                    
                                                    <div className="message-content">
                                                        <div
                                                            className='message-text'
                                                            dangerouslySetInnerHTML={{ __html: msg.texto }}
                                                        />
                                                        
                                                        {msg.archivos && msg.archivos.length > 0 && (
                                                            <div className="message-files">
                                                                {msg.archivos.map((file, i) => {
                                                                    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file);
                                                                    const isPDF = /\.pdf$/i.test(file);
                                                                    const isDoc = /\.(doc|docx)$/i.test(file);
                                                                    const canPreview = isImage || isPDF;
                                                                    
                                                                    return (
                                                                        <div 
                                                                            key={i}
                                                                            className='message-file-item'
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                if (canPreview) {
                                                                                    setFilePreview({ url: file, type: isImage ? 'image' : 'pdf' });
                                                                                } else {
                                                                                    window.open(file, '_blank');
                                                                                }
                                                                            }}
                                                                            style={{ cursor: 'pointer' }}
                                                                        >
                                                                            <img src={getIconUrl(file)} alt="Archivo" />
                                                                            <span>{getFileName(file, 20)}</span>
                                                                            {canPreview && (
                                                                                <svg className="preview-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                                                    <circle cx="12" cy="12" r="3"/>
                                                                                </svg>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    <div className="message-footer">
                                                        <span className='message-time'>
                                                            {new Date(msg.fecha).toLocaleTimeString('es-ES', {
                                                                hour: '2-digit', minute: '2-digit', hour12: false
                                                            })}
                                                        </span>
                                                        {esMio && (
                                                            <span className="message-status">
                                                                {msg._isTemp ? (
                                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                        <path d="M8 1C4.134 1 1 4.134 1 8s3.134 7 7 7 7-3.134 7-7-3.134-7-7-7zm0 12.5c-3.033 0-5.5-2.467-5.5-5.5S4.967 2.5 8 2.5s5.5 2.467 5.5 5.5-2.467 5.5-5.5 5.5z" fill="currentColor" opacity="0.5"/>
                                                                        <path d="M8 2.5v5.5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                                                    </svg>
                                                                ) : (
                                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                        <path d="M1.5 8.5l3 3 9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                    </svg>
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {estaExpandido && (
                                                        <div className="message-expanded-info">
                                                            <div className="expanded-info-row">
                                                                <span className="info-label">Usuario:</span>
                                                                <span className="info-value">@{msg.usuario}</span>
                                                            </div>
                                                            <div className="expanded-info-row">
                                                                <span className="info-label">Fecha completa:</span>
                                                                <span className="info-value">
                                                                    {new Date(msg.fecha).toLocaleString('es-ES', {
                                                                        day: '2-digit',
                                                                        month: 'long',
                                                                        year: 'numeric',
                                                                        hour: '2-digit',
                                                                        minute: '2-digit',
                                                                        hour12: false
                                                                    })}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {esMio && mostrarAvatar && (
                                                    <img 
                                                        className="message-avatar" 
                                                        src={msg.fotoPerfil ? (msg.fotoPerfil.startsWith('http') ? msg.fotoPerfil : `${IMAGE_DOMAIN}${msg.fotoPerfil}`) : `${IMAGE_DOMAIN}/images/usuario.png`} 
                                                        alt="Avatar" 
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className='bottomRef' ref={bottomRef}></div>
                            </div>
                            <div className="enviar-mensaje">
                                {ticket.estado_del_ticket === 'cerrado' && (
                                    <p className="reopen-notice">Este ticket ha sido cerrado. Para reabrirlo, vuelve a enviar un mensaje.</p>
                                )}
                                {usuarioEscribiendo && (
                                    <div className="escribiendo">
                                        <img src="/images/chat-escribiendo.svg" alt="Escribiendo" />
                                        <p>{usuarioEscribiendo}</p>
                                    </div>
                                )}
                                {tokenInfo.role === "admin" &&
                                    ticket.usuario_encargado === "sin_asignar" ? (
                                    <div className='chat-deshabilitado-admin'>
                                        <div className='mensaje-deshabilitado'>
                                            <p>Asigna el ticket a un técnico para habilitar el chat.</p>
                                        </div>
                                        <ReactQuill 
                                            theme="snow" 
                                            value={content} 
                                            onChange={handleChange}
                                            modules={quillModules}
                                            formats={quillFormats}
                                        />
                                        <button className='enviar-ticket' onClick={enviarMensaje} disabled={isSending}>
                                            {isSending ? "Enviando..." : "Enviar"}
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        {
                                            (tokenInfo.role === "admin" || tokenInfo.role === "tecnico") && (
                                                <div className="mensajes-preconfigurados">
                                                    <p>Mensajes preconfigurados</p>
                                                    <div className="mensajes-preconfigurados-lista">
                                                        <button onClick={() => setContent(`¡Hola ${ticket.nombre}!<br/><br/>¿En qué podemos ayudarte?`)}>Mensaje de bienvenida</button>
                                                        <button
                                                            onClick={() =>
                                                                setContent(
                                                                    `¡Hola ${ticket.nombre}!<br/><br/>Hemos recibido tu solicitud y nuestro equipo ya la está revisando en detalle. Te mantendremos al tanto y nos pondremos en contacto contigo a la mayor brevedad por este medio.<br/><br/>Un saludo,<br/>${tokenInfo.nombre}`
                                                                )
                                                            }
                                                        >
                                                            Procesando solicitud
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                setContent(
                                                                    `¡Hola de nuevo ${ticket.nombre}!<br/><br/>Acabamos de resolver tu incidencia. ¿Podrías revisar que todo esté correcto? ¡Gracias!<br/><br/>Un saludo,<br/>${tokenInfo.nombre}`
                                                                )
                                                            }
                                                        >
                                                            Incidencia resuelta
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                setContent(
                                                                    `¡Hola de nuevo ${ticket.nombre}!<br/><br/>Hemos completado la tarea que nos solicitaste. ¿Podrías revisar que todo esté correcto? ¡Gracias!<br/><br/>Un saludo,<br/>${tokenInfo.nombre}`
                                                                )
                                                            }
                                                        >
                                                            Tarea resuelta
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                setContent(
                                                                    `¡Hola ${ticket.nombre}!<br/><br/>El asunto de tu ticket corresponde a otro departamento, lo escalamos al departamento correspondiente y el equipo continuará con el seguimiento. ¡Gracias!<br/><br/>Un saludo,<br/>${tokenInfo.nombre}`
                                                                )
                                                            }
                                                        >
                                                            Escala a otro departamento
                                                        </button>
                                                    </div>
                                                </div>
                                            )
                                        }
                                        <div className="gestor-archivo-chat">
                                            {errorAlEnviar && (
                                                <p className='error error-chat'>El mensaje no puede estar vacío</p>
                                            )}
                                            <label 
                                                className={`custum-file-upload ${isDragging ? 'dragging' : ''}`}
                                                htmlFor="file"
                                                onDragEnter={handleDragEnter}
                                                onDragOver={handleDragOver}
                                                onDragLeave={handleDragLeave}
                                                onDrop={handleDrop}
                                            >
                                                <div className="icon">
                                                    <img src="/images/archive.svg" alt="Adjuntar" />
                                                </div>
                                                <div className="text">
                                                    <span>{isDragging ? 'Suelta los archivos aquí' : 'Arrastra archivos o haz clic para adjuntar (máx. 5MB por archivo)'}</span>
                                                </div>
                                                <input type="file" id="file" multiple onChange={handleFileChange} />
                                            </label>
                                            <div className="file-list-ticket">
                                                {selectedFiles.map((file, index) => (
                                                    <div key={index} className="file">
                                                        <img src="/images/nuevo-documento.svg" alt="Archivo" />
                                                        <p>{file.name}</p>
                                                        <button className="remove-file" onClick={() => handleRemoveFile(index)}>x</button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <ReactQuill 
                                            theme="snow" 
                                            value={content} 
                                            onChange={handleChange}
                                            modules={quillModules}
                                            formats={quillFormats}
                                        />
                                        <button className={isSending ? "enviar-ticket cooldown" : "enviar-ticket"} onClick={enviarMensaje} disabled={isSending}>
                                            {isSending ? "Enviando..." : "Enviar"}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
            {filePreview && (
                <>
                    <div className="layer-black" onClick={() => setFilePreview(null)}></div>
                    <div className="file-preview-modal">
                        <div className="file-preview-content">
                            <button className="close-preview" onClick={() => setFilePreview(null)}>✕</button>
                            
                            {filePreview.type === 'image' ? (
                                <img src={filePreview.url} alt="Preview" className="preview-image" />
                            ) : filePreview.type === 'pdf' ? (
                                <iframe 
                                    src={filePreview.url} 
                                    className="preview-pdf"
                                    title="PDF Preview"
                                />
                            ) : null}
                            
                            <div className="file-preview-actions">
                                <button onClick={() => descargarArchivo(filePreview.url)} className="download-btn">
                                    <img src="/images/descargar.svg" alt="Descargar" />
                                    Descargar {filePreview.type === 'image' ? 'imagen' : 'archivo'}
                                </button>
                                <a href={filePreview.url} target="_blank" rel="noopener noreferrer" className="open-btn">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                        <polyline points="15 3 21 3 21 9"/>
                                        <line x1="10" y1="14" x2="21" y2="3"/>
                                    </svg>
                                    Abrir en nueva pestaña
                                </a>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <ToastContainer position="bottom-right" autoClose={3000} />
        </main>
    );
};

export default Ticket;
